import React from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import styles from './ScheduleTables.module.scss';

const ClinicalSiteScheduleTable = ({ data = [], weekDates = [], isLoading, onCellClick }) => {
  const colors = ['green', 'orange', 'red', 'purple', 'blue', 'pink', 'teal'];
  const getColor = (id) => colors[id % colors.length];

  const getScheduleForDate = (site, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return site.schedules?.find(s => s.date === dateStr);
  };

  const getProgressBarColor = (booked, capacity) => {
    if (capacity > 0 && booked > capacity) return 'red';
    if (booked > 0) return 'blue';
    return 'gray';
  };

  if (isLoading && data.length === 0) {
    return <div className={styles.loading}>Loading clinical site schedules...</div>;
  }

  return (
    <div className={styles.tableWrapper}>
      <table className={isLoading ? styles.tableLoading : ''}>
        <thead>
          <tr>
            <th>Clinical Site</th>
            <th>Specialty</th>
            <th>Year</th>
            {weekDates.map((date, idx) => {
              const today = new Date();
              const isToday = format(date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
              return (
                <th key={idx}>
                  <div className={`${styles.dateHeader} ${isToday ? styles.activeDate : ''}`}>
                    <span className={styles.dayNum}>{format(date, 'd')}</span>
                    <span className={styles.dayStr}>{format(date, 'EEE')}</span>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.id}>
              <td>
                <span 
                  className={`${styles.pill} ${styles.outline} ${styles[getColor(row.id)]}`}
                  title={row.name}
                >
                  {row.name}
                </span>
              </td>
              <td>
                <div className={styles.pillList}>
                  {row.specialities?.map(spec => (
                    <span 
                      key={spec.id} 
                      className={`${styles.pill} ${styles.solid} ${styles[getColor(spec.id)]}`}
                      title={spec.name}
                    >
                      {spec.name}
                    </span>
                  ))}
                  {(!row.specialities || row.specialities.length === 0) && <span className={styles.empty}>-</span>}
                </div>
              </td>
              <td>
                <div className={styles.supportedYears}>
                  {row.supportedYears?.map(y => (
                    <div key={y.id} className={styles.yearItem}>{y.year}</div>
                  ))}
                  {(!row.supportedYears || row.supportedYears.length === 0) && <span>-</span>}
                </div>
              </td>
              {weekDates.map((date, dIdx) => {
                const daySchedule = getScheduleForDate(row, date);
                if (!daySchedule) return <td key={dIdx} className={styles.emptyDay}>-</td>;

                const { bookedStudents, totalCapacity, students } = daySchedule;
                const pct = totalCapacity > 0 ? Math.min((bookedStudents / totalCapacity) * 100, 100) : (bookedStudents > 0 ? 100 : 0);
                const color = getProgressBarColor(bookedStudents, totalCapacity);

                return (
                  <td
                    key={dIdx}
                    className={`${bookedStudents > 0 ? styles.allocatedCell : ''} allocated-cell`}
                    onClick={(e) => bookedStudents > 0 && onCellClick(e, students, row.name, format(date, 'dd EEE, MMMM yyyy'))}
                  >
                    <div className={styles.progressCell}>
                      <div className={styles.progressBar}>
                        <span>{`${bookedStudents}/${totalCapacity}`}</span>
                        <div className={styles.track}>
                          <div 
                            className={`${styles.fill} ${styles[color]}`} 
                            style={{ width: `${pct}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
          {data.length === 0 && !isLoading && (
            <tr>
              <td colSpan={weekDates.length + 3} className={styles.noData}>
                No clinical site data found for this period.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

ClinicalSiteScheduleTable.propTypes = {
  data: PropTypes.array,
  weekDates: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  isLoading: PropTypes.bool,
  onCellClick: PropTypes.func
};

export default ClinicalSiteScheduleTable;
